/**
 * @fileoverview Application routing with code splitting and lazy loading
 * All routes are lazy-loaded with Suspense boundaries for optimal performance
 */

import { MainLayout } from '@/components/layout/MainLayout'
import {
  CalendarSkeleton,
  ChartPageSkeleton,
  DashboardSkeleton,
  FoodDetailsSkeleton,
  FormSkeleton,
  GenericSkeleton,
  ProfileSkeleton,
  TableSkeleton,
} from '@/components/ui/LoadingSkeletons'
import { Suspense, lazy } from 'react'
import { Route, Routes } from 'react-router-dom'

// Lazy-load all page components for optimal code splitting
// Each page becomes its own bundle, loaded only when needed

// Core pages
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Statistics = lazy(() => import('@/pages/Statistics'))
const Calendar = lazy(() => import('@/pages/Calendar'))

// Chart pages (heavy dependencies: recharts)
const WeeklyNutritionCharts = lazy(() => import('@/pages/WeeklyNutritionCharts'))
const BodyMeasurements = lazy(() => import('@/pages/BodyMeasurements'))
const Fiber = lazy(() => import('@/pages/Fiber'))

// Data management pages
const NutritionList = lazy(() => import('@/pages/NutritionList'))
const Foods = lazy(() => import('@/pages/Foods'))
const FoodDetails = lazy(() => import('@/pages/FoodDetails'))

// Utility pages
const Utils = lazy(() => import('@/pages/Utils'))
const ItemCounter = lazy(() => import('@/pages/ItemCounter'))
const Profile = lazy(() => import('@/pages/Profile'))

// Error pages (lightweight, can be eagerly loaded)
const NotFound = lazy(() => import('@/pages/NotFound'))

/**
 * Main application routes with lazy loading and appropriate loading skeletons
 */
export function AppRoutes() {
  return (
    <MainLayout>
      <Routes>
        {/* Core pages */}
        <Route
          path="/"
          element={
            <Suspense fallback={<DashboardSkeleton />}>
              <Dashboard />
            </Suspense>
          }
        />
        <Route
          path="/statistics"
          element={
            <Suspense fallback={<TableSkeleton />}>
              <Statistics />
            </Suspense>
          }
        />
        <Route
          path="/calendar"
          element={
            <Suspense fallback={<CalendarSkeleton />}>
              <Calendar />
            </Suspense>
          }
        />

        {/* Chart pages - heavy dependencies */}
        <Route
          path="/weekly-charts"
          element={
            <Suspense fallback={<ChartPageSkeleton />}>
              <WeeklyNutritionCharts />
            </Suspense>
          }
        />
        <Route
          path="/body-measurements"
          element={
            <Suspense fallback={<ChartPageSkeleton />}>
              <BodyMeasurements />
            </Suspense>
          }
        />
        <Route
          path="/fiber"
          element={
            <Suspense fallback={<ChartPageSkeleton />}>
              <Fiber />
            </Suspense>
          }
        />

        {/* Data management pages */}
        <Route
          path="/utils/nutrition-list"
          element={
            <Suspense fallback={<TableSkeleton />}>
              <NutritionList />
            </Suspense>
          }
        />
        <Route
          path="/foods"
          element={
            <Suspense fallback={<TableSkeleton />}>
              <Foods />
            </Suspense>
          }
        />
        <Route
          path="/foods/:foodId"
          element={
            <Suspense fallback={<FoodDetailsSkeleton />}>
              <FoodDetails />
            </Suspense>
          }
        />

        {/* Utility pages */}
        <Route
          path="/utils"
          element={
            <Suspense fallback={<GenericSkeleton />}>
              <Utils />
            </Suspense>
          }
        />
        <Route
          path="/utils/item-counter"
          element={
            <Suspense fallback={<FormSkeleton />}>
              <ItemCounter />
            </Suspense>
          }
        />
        <Route
          path="/profile"
          element={
            <Suspense fallback={<ProfileSkeleton />}>
              <Profile />
            </Suspense>
          }
        />

        {/* Error pages */}
        <Route
          path="*"
          element={
            <Suspense fallback={<GenericSkeleton />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </MainLayout>
  )
}
